import { useState, useEffect } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { FileText, Copy, RotateCw, Image, ArrowLeft, Save, Send, Download } from 'lucide-react';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { API_URL } from '../config';

export default function ArticleEditor() {
  const navigate = useNavigate();
  const { id, articleId } = useParams();
  const { user } = useAuth();
  const [isDownloading, setIsDownloading] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [loading, setLoading] = useState(true);
  
  // State for content
  const [content, setContent] = useState(null); // Remove hardcoded mock data

  const [selectedImage, setSelectedImage] = useState(1);

  useEffect(() => {
    if (user && id && articleId) {
      fetchArticle();
    }
  }, [id, articleId, user]);

  const fetchArticle = async () => {
    try {
      const res = await axios.get(`${API_URL}/tasks/${id}/articles`, {
        headers: { 'x-user-email': user?.email }
      });
      if (res.data) {
        // Ensure type consistency for comparison
        const article = res.data.find(a => String(a.id) === String(articleId));
        if (article) {
          setContent(article);
        } else {
            console.error(`Article ${articleId} not found in task ${id}`);
            alert('文章不存在');
            navigate(-1);
        }
      }
    } catch (err) {
      console.error("Failed to fetch article", err);
    } finally {
      setLoading(false);
    }
  };

  const handleSaveDraft = async () => {
    setIsSaving(true);
    try {
        await axios.patch(`${API_URL}/tasks/${id}/articles/${articleId}`, {
            content
        }, {
            headers: { 'x-user-email': user?.email }
        });
        alert('草稿已保存！');
    } catch (err) {
        console.error("Save failed", err);
        alert('保存失败，请重试');
    } finally {
        setIsSaving(false);
    }
  };

  const handlePublish = () => {
    alert('文章已发布！');
    navigate('/tasks');
  };

  const handleDownload = async () => {
    setIsDownloading(true);
    const zip = new JSZip();

    try {
      // 1. Text Content
      const textBody = `标题：${content.title || ''}\n\n${content.content || content.body || ''}`;

      zip.file(`${content.title || 'article'}.txt`, textBody);

      // 2. Images
      const imgFolder = zip.folder("images");
      const baseUrl = API_URL.replace('/api', '');

      if (content.images && Array.isArray(content.images)) {
        for (let i = 0; i < content.images.length; i++) {
          let imgPath = content.images[i];
          if (!imgPath) continue;
          if (imgPath.startsWith('/')) {
            imgPath = baseUrl + imgPath;
          }
          try {
            const response = await fetch(imgPath);
            if (response.ok) {
              const fileBlob = await response.blob();
              const prefix = (i === selectedImage) ? "selected_" : "";
              imgFolder.file(`${prefix}image_${i + 1}.jpg`, fileBlob);
            }
          } catch (err) {
            console.error("Failed to fetch image:", imgPath, err);
          }
        }
      }

      // Generate ZIP
      const blob = await zip.generateAsync({ type: "blob" });
      saveAs(blob, `${content.title || 'article'}_assets.zip`);

    } catch (error) {
      console.error("Download failed:", error);
      alert("下载失败，请重试");
    } finally {
      setIsDownloading(false);
    }
  };

  if (loading) return <div className="flex items-center justify-center h-screen bg-gray-50 text-gray-500">加载文章中...</div>;
  if (!content) return <div className="flex items-center justify-center h-screen bg-gray-50 text-red-500">无法加载文章内容，请稍后重试。</div>;

  return (
    <div className="flex flex-col h-screen overflow-hidden bg-gray-50">
      {/* Header */}
      <header className="h-16 bg-white border-b border-gray-200 flex items-center justify-between px-6 shadow-sm flex-shrink-0 z-20">
        <div className="flex items-center space-x-4">
          <button 
            onClick={() => navigate(-1)}
            className="p-2 hover:bg-gray-100 rounded-full text-gray-500 transition-colors"
          >
            <ArrowLeft size={20} />
          </button>
          <h1 className="text-lg font-bold text-gray-800 truncate max-w-md">{content.title}</h1>
          <span className="px-2 py-0.5 bg-blue-50 text-blue-600 text-xs font-medium rounded border border-blue-100">
            评分 {content.score || '8.5'}
          </span>
        </div>
        <div className="flex items-center space-x-3">
           <button 
            onClick={handleSaveDraft}
            disabled={isSaving}
            className="px-4 py-2 text-gray-600 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2 disabled:opacity-50"
           >
            <Save size={16} />
            <span>{isSaving ? '保存中...' : '保存草稿'}</span>
          </button>
          <button 
            onClick={handleDownload}
            disabled={isDownloading}
            className="px-4 py-2 text-gray-700 bg-white border border-gray-200 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors flex items-center space-x-2 disabled:opacity-50"
          >
            <Download size={16} />
            <span>{isDownloading ? '打包中...' : '下载素材'}</span>
          </button>
          <button 
            onClick={handlePublish}
            className="px-5 py-2 bg-gradient-to-r from-blue-500 to-blue-400 text-white rounded-lg text-sm font-bold shadow-md hover:shadow-lg transition-all flex items-center space-x-2"
          >
            <Send size={16} />
            <span>发布文章</span>
          </button>
        </div>
      </header>

      {/* Main Content */}
      <main className="flex-1 flex overflow-hidden w-full max-w-7xl mx-auto p-6 gap-6">
        
        {/* Left: Text Editor */}
        <div className="flex-1 bg-white rounded-2xl shadow-sm border border-gray-100 overflow-y-auto custom-scrollbar flex flex-col">
          <div className="p-6 border-b border-gray-50 flex justify-between items-center sticky top-0 bg-white z-10">
            <h2 className="text-lg font-bold flex items-center text-gray-800">
              <FileText className="mr-2 text-[#256df3]" size={20} />
              文字内容
            </h2>
            <button className="text-xs text-blue-600 font-medium hover:underline flex items-center">
              <Copy size={12} className="mr-1" />
              复制全文
            </button>
          </div>
          
          <div className="p-6 space-y-6 flex-1 flex flex-col">
            {/* Field: Title */}
            <div className="group">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">标题</label>
              <input 
                type="text" 
                value={content.title || ''} 
                onChange={(e) => setContent({...content, title: e.target.value})}
                className="w-full text-xl font-bold text-gray-800 border-none outline-none focus:ring-0 bg-transparent placeholder-gray-300" 
              />
              <div className="h-px bg-gray-100 mt-2 group-focus-within:bg-blue-500 transition-colors"></div>
            </div>

            {/* Field: Full Content */}
            <div className="flex-1 flex flex-col">
              <label className="block text-xs font-bold text-gray-400 uppercase tracking-wider mb-2">正文内容</label>
              <textarea 
                value={content.content || content.body || ''}
                onChange={(e) => setContent({...content, content: e.target.value})}
                className="w-full flex-1 text-sm text-gray-600 leading-relaxed border-none outline-none resize-none focus:ring-0 bg-gray-50/50 p-4 rounded-lg"
                placeholder="文章内容将在这里显示..."
              />
            </div>
          </div>
        </div>

        {/* Right: Image Selector */}
        <div className="w-[400px] xl:w-[480px] flex flex-col gap-4 flex-shrink-0 h-full overflow-hidden">
          
          {/* Selected Image Preview (Constrained Height) */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 flex flex-col flex-shrink-0">
            <div className="p-3 border-b border-gray-50 flex justify-between items-center">
              <h2 className="text-sm font-bold flex items-center text-gray-800">
                <Image className="mr-2 text-[#256df3]" size={18} />
                当前预览
              </h2>
            </div>
            {/* Image Container */}
            <div className="bg-gray-50 relative group flex items-center justify-center w-full aspect-[3/4]">
              <div className="h-full w-full flex items-center justify-center p-4">
                {content.images && content.images.length > 0 ? (
                    <img 
                      src={((content.images[selectedImage] || content.images[0]).startsWith('http') ? '' : API_URL.replace('/api', '')) + (content.images[selectedImage] || content.images[0])} 
                      className="w-full h-full object-contain rounded shadow-sm p-2"
                      alt="Preview"
                      onError={(e) => {
                          e.target.onerror = null;
                          e.target.src = "https://via.placeholder.com/300?text=Image+Load+Error";
                      }}
                    />
                ) : (
                    <div className="text-gray-400 text-sm">暂无配图</div>
                )}
              </div>
            </div>
          </div>

          {/* Carousel / Options */}
          <div className="bg-white rounded-2xl shadow-sm border border-gray-100 p-4 flex-1 overflow-y-auto custom-scrollbar flex flex-col">
            <div className="flex justify-between items-center mb-3">
              <h3 className="text-sm font-bold text-gray-700">生成配图 ({content.images?.length || 0})</h3>
            </div>
            
            <div className="grid grid-cols-3 gap-3 pb-4">
              {content.images && content.images.map((imgUrl, idx) => (
                <div 
                    key={idx}
                    onClick={() => setSelectedImage(idx)}
                    className={`aspect-[3/4] rounded-xl bg-gray-50 cursor-pointer relative border-2 transition-all overflow-hidden ${
                        selectedImage === idx 
                        ? 'border-blue-500 ring-4 ring-blue-50 shadow-md' 
                        : 'border-transparent hover:border-blue-300 hover:shadow-md'
                    }`}
                >
                  <img 
                    src={(imgUrl.startsWith('http') ? '' : API_URL.replace('/api', '')) + imgUrl}
                    className="w-full h-full object-cover"
                    loading="lazy"
                    onError={(e) => { e.target.onerror = null; e.target.src = "https://via.placeholder.com/150?text=Error"; }}
                  />
                  {selectedImage === idx && (
                    <div className="absolute top-2 left-2 px-2 py-0.5 bg-blue-500 text-white text-[10px] font-bold rounded shadow-sm">当前</div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

      </main>
    </div>
  );
}